import z from "zod";

import { OrderTypes } from "./supabase/enums";
import type { MenuCourse } from "./products";

/**
 * Menu picks: what a guest chose for each course of a menu product.
 *
 * A course takes `pickCount` picks per menu (a "menu for 2" asks for two
 * starters), the same item may be picked more than once, and every pick
 * becomes its own order line under the menu line with its own options.
 *
 * On the wire and in the cart a menu carries `menuPicks`
 * (courseId -> picks). Carts persisted before picks existed, and clients
 * still running an older bundle, send the legacy one-item-per-course shape
 * instead: `menuSelections` (courseId -> productId) plus
 * `menuOptionSelections` (courseId -> { subgroupId -> optionId(s) }). When
 * `menuPicks` is present it is authoritative; otherwise the legacy fields are
 * read as one pick per course.
 */

/** The most picks a course can ask for (menu_course_setting.pick_count). */
export const MAX_MENU_PICK_COUNT = 20;

export interface MenuPick {
  productId: string;
  // subgroupId -> optionId, or comma-joined optionIds for a multi-select
  // subgroup; the same shape as a product's `selectedOptions`.
  optionSelections?: Record<string, string>;
}

export type MenuPicks = Record<string, MenuPick[]>;

export interface MenuPickSource {
  menuPicks?: MenuPicks;
  menuSelections?: Record<string, string>;
  menuOptionSelections?: Record<string, Record<string, string>>;
}

export const menuPicksSchema = z.record(
  z.string(),
  z
    .array(
      z.object({
        productId: z.string(),
        optionSelections: z.record(z.string(), z.string()).optional(),
      })
    )
    .max(MAX_MENU_PICK_COUNT)
);

/** A course's pick count from its setting row: 1 when unset or out of range. */
export function normalizePickCount(value: unknown): number {
  const count = typeof value === "number" ? Math.floor(value) : Number.NaN;
  if (!Number.isFinite(count) || count < 1) return 1;
  return Math.min(count, MAX_MENU_PICK_COUNT);
}

function splitOptionValue(value: string | undefined | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

function hasEntries(value: Record<string, string> | undefined) {
  return !!value && Object.keys(value).length > 0;
}

/**
 * The picks per course of a cart item or order item, whichever shape it
 * carries. Courses without a pick are left out.
 */
export function getMenuPicks(source: MenuPickSource): MenuPicks {
  const picks: MenuPicks = {};

  if (source.menuPicks) {
    for (const [courseId, coursePicks] of Object.entries(source.menuPicks)) {
      const valid = (coursePicks ?? []).filter(
        (pick) => typeof pick?.productId === "string" && pick.productId !== ""
      );
      if (valid.length > 0) picks[courseId] = valid;
    }
    return picks;
  }

  for (const [courseId, productId] of Object.entries(
    source.menuSelections ?? {}
  )) {
    if (!productId) continue;
    const optionSelections = source.menuOptionSelections?.[courseId];
    picks[courseId] = [
      hasEntries(optionSelections)
        ? { productId, optionSelections }
        : { productId },
    ];
  }
  return picks;
}

/** Every picked product id (repeats included). */
export function getMenuPickProductIds(source: MenuPickSource): string[] {
  return Object.values(getMenuPicks(source)).flatMap((coursePicks) =>
    coursePicks.map((pick) => pick.productId)
  );
}

/** Every option id chosen on any pick (repeats included). */
export function getMenuPickOptionIds(source: MenuPickSource): string[] {
  return Object.values(getMenuPicks(source)).flatMap((coursePicks) =>
    coursePicks.flatMap((pick) =>
      Object.values(pick.optionSelections ?? {}).flatMap(splitOptionValue)
    )
  );
}

function normalizedOptions(optionSelections: Record<string, string> = {}) {
  return Object.entries(optionSelections)
    .map(
      ([subgroupId, value]) =>
        [subgroupId, splitOptionValue(value).sort().join(",")] as const
    )
    .filter(([, value]) => value !== "")
    .sort(([a], [b]) => a.localeCompare(b));
}

function pickIdentity(pick: MenuPick): string {
  return JSON.stringify([pick.productId, normalizedOptions(pick.optionSelections)]);
}

/**
 * A stable fingerprint of a menu's picks: the same items with the same
 * options give the same string, whatever order they were picked or their
 * options ticked in, and whichever shape (picks or legacy) carries them.
 */
export function getMenuPicksIdentity(source: MenuPickSource): string {
  const courses = Object.entries(getMenuPicks(source))
    .map(
      ([courseId, coursePicks]) =>
        [courseId, coursePicks.map(pickIdentity).sort()] as const
    )
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(courses);
}

/**
 * The picks of a course an order is built from: only those `isOffered`
 * accepts (a real item of this course), and never more than the course takes.
 * Anything beyond is dropped, just like a pick the course does not offer.
 */
export function selectOfferedMenuPicks(
  coursePicks: MenuPick[],
  pickCount: number,
  isOffered: (pick: MenuPick) => boolean
): MenuPick[] {
  const limit = normalizePickCount(pickCount);
  const offered: MenuPick[] = [];
  for (const pick of coursePicks) {
    if (offered.length >= limit) break;
    if (isOffered(pick)) offered.push(pick);
  }
  return offered;
}

// Price of a menu item option for the given order type (product-type options
// carry take-away/delivery prices; plain options use their single price).
export function getMenuOptionPrice(
  option: { price: number; takeAwayPrice?: number; deliveryPrice?: number },
  orderType: OrderTypes
): number {
  if (
    option.takeAwayPrice !== undefined ||
    option.deliveryPrice !== undefined
  ) {
    return orderType === OrderTypes.pickup
      ? (option.takeAwayPrice ?? 0)
      : (option.deliveryPrice ?? 0);
  }
  return option.price;
}

export interface SelectedMenuItem {
  // Unique within the menu: course plus what was picked.
  key: string;
  courseId: string;
  courseName: string;
  itemName: string;
  // How many identical picks (same item, same options) this row stands for.
  quantity: number;
  // Per pick.
  priceSupplement: number;
  options: Array<{ id: string; name: string; price: number }>;
}

/**
 * The picks resolved against the menu's courses for display and pricing, in
 * course order. Identical picks of a course are shown once with a quantity.
 * Picks that no longer match a course or item are dropped.
 */
export function resolveSelectedMenuItems(
  courses: MenuCourse[] | undefined,
  source: MenuPickSource,
  orderType: OrderTypes
): SelectedMenuItem[] {
  if (!courses || courses.length === 0) return [];
  const picks = getMenuPicks(source);
  const result: SelectedMenuItem[] = [];

  courses.forEach((course) => {
    const rowsByIdentity = new Map<string, SelectedMenuItem>();
    (picks[course.courseId] ?? []).forEach((pick) => {
      const item = course.items.find(
        (candidate) => candidate.productId === pick.productId
      );
      if (!item) return;

      const identity = pickIdentity(pick);
      const existing = rowsByIdentity.get(identity);
      if (existing) {
        existing.quantity += 1;
        return;
      }

      const options: SelectedMenuItem["options"] = [];
      Object.values(pick.optionSelections ?? {}).forEach((value) => {
        splitOptionValue(value).forEach((optionId) => {
          for (const subgroup of item.productOptionSubgroups) {
            const option = subgroup.productOptions?.find(
              (candidate) => candidate.id === optionId
            );
            if (option) {
              options.push({
                id: option.id,
                name: option.name,
                price: getMenuOptionPrice(option, orderType),
              });
              break;
            }
          }
        });
      });

      const row: SelectedMenuItem = {
        key: `${course.courseId}:${identity}`,
        courseId: course.courseId,
        courseName: course.courseName,
        itemName: item.name,
        quantity: 1,
        priceSupplement: item.priceSupplement,
        options,
      };
      rowsByIdentity.set(identity, row);
      result.push(row);
    });
  });

  return result;
}

/** Supplements plus option prices of all picks, for one menu. */
export function getMenuPicksExtrasTotal(
  courses: MenuCourse[] | undefined,
  source: MenuPickSource,
  orderType: OrderTypes
): number {
  return resolveSelectedMenuItems(courses, source, orderType).reduce(
    (total, row) =>
      total +
      row.quantity *
        (row.priceSupplement +
          row.options.reduce((sum, option) => sum + option.price, 0)),
    0
  );
}

// ---- Menu dialog form state -> picks ----

/**
 * Radio value of the "no selection" choice on an optional single-pick course.
 * Empty string is avoided because Radix radio items need a concrete value.
 */
export const MENU_NO_SELECTION = "__none__";

/** A course the guest picks several items for (a stepper per item). */
export function isMultiPickCourse(course: { pickCount?: number }): boolean {
  return (course.pickCount ?? 1) > 1;
}

type FormOptionValue = string | string[] | null | undefined;

/**
 * The menu dialog's form state. A single-pick course keeps today's radio
 * shape: `menuSelections[courseId]` with its options under
 * `menuOptionSelections[courseId]`. A multi-pick course lists its picks under
 * `menuPicks[courseId]`, each with a unique key its options are stored under
 * (`menuOptionSelections[pick.key]`).
 */
export interface MenuPickFormValues {
  menuSelections?: Record<string, string | null | undefined>;
  menuOptionSelections?: Record<
    string,
    Record<string, FormOptionValue> | undefined
  >;
  menuPicks?: Record<
    string,
    Array<{ key: string; productId: string }> | undefined
  >;
}

function cleanFormOptions(
  values: Record<string, FormOptionValue> | undefined
): Record<string, string> | undefined {
  const cleaned: Record<string, string> = {};
  Object.entries(values ?? {}).forEach(([subgroupId, value]) => {
    if (Array.isArray(value)) {
      if (value.length > 0) cleaned[subgroupId] = value.join(",");
    } else if (typeof value === "string" && value !== "") {
      cleaned[subgroupId] = value;
    }
  });
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

/** The dialog's form state as the picks a cart item carries. */
export function collectMenuPicks(
  courses: Array<{ courseId: string; pickCount?: number }>,
  values: MenuPickFormValues
): MenuPicks {
  const picks: MenuPicks = {};

  courses.forEach((course) => {
    const coursePicks: MenuPick[] = [];

    if (isMultiPickCourse(course)) {
      (values.menuPicks?.[course.courseId] ?? [])
        .slice(0, normalizePickCount(course.pickCount))
        .forEach((pick) => {
          if (!pick.productId) return;
          const optionSelections = cleanFormOptions(
            values.menuOptionSelections?.[pick.key]
          );
          coursePicks.push(
            optionSelections
              ? { productId: pick.productId, optionSelections }
              : { productId: pick.productId }
          );
        });
    } else {
      const productId = values.menuSelections?.[course.courseId];
      if (
        typeof productId === "string" &&
        productId !== "" &&
        productId !== MENU_NO_SELECTION
      ) {
        const optionSelections = cleanFormOptions(
          values.menuOptionSelections?.[course.courseId]
        );
        coursePicks.push(
          optionSelections ? { productId, optionSelections } : { productId }
        );
      }
    }

    if (coursePicks.length > 0) picks[course.courseId] = coursePicks;
  });

  return picks;
}
